import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAdminAuth } from '../../context/AdminAuthContext';
import axios from 'axios';
import { 
  Users, 
  Search,
  ChevronLeft,
  Clock,
  Mail,
  Phone,
  MapPin,
  FileText,
  Menu,
  X,
  Save
} from 'lucide-react';
import { API_BASE_URL } from '@/config/api';
import AdminSidebar from '@/components/admin/AdminSidebar';

const formTypeLabels = {
  contact: 'Contact Form',
  off_market: 'Off Market Access',
  estimation: 'Property Estimation',
  property_inquiry: 'Property Inquiry',
  acheter_search: 'Buyer Search',
  louer_search: 'Rental Search',
  recherche: 'Advanced Search'
};

const statusColors = {
  new: 'bg-blue-50 text-blue-700',
  contacted: 'bg-yellow-50 text-yellow-700',
  qualified: 'bg-green-50 text-green-700',
  archived: 'bg-gray-100 text-gray-600'
};

const AdminLeads = () => {
  const { admin, logout, loading: authLoading } = useAdminAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [filters, setFilters] = useState({
    form_type: '',
    status: '',
    search: '',
    date_from: '',
    date_to: '',
    property_id: '',
  });
  const [properties, setProperties] = useState([]);

  useEffect(() => {
    if (!authLoading && !admin) {
      navigate('/admin/login');
    }
  }, [admin, authLoading, navigate]);

  useEffect(() => {
    if (admin) {
      fetchLeads();
    }
  }, [admin, filters]);

  useEffect(() => {
    if (admin) {
      fetchProperties();
    }
  }, [admin]);

  const fetchProperties = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/admin/properties`,
        { withCredentials: true }
      );
      setProperties(response.data);
    } catch (error) {
      console.error('Error fetching properties for filter:', error);
    }
  };

  const fetchLeads = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.form_type) params.append('form_type', filters.form_type);
      if (filters.status) params.append('status', filters.status);
      if (filters.search) params.append('search', filters.search);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      if (filters.property_id) params.append('property_id', filters.property_id);
      
      const response = await axios.get(
        `${API_BASE_URL}/api/admin/leads?${params.toString()}`,
        { withCredentials: true }
      );
      setLeads(response.data);
    } catch (error) {
      console.error('Error fetching leads:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#F5F3EF] flex items-center justify-center">
        <div className="animate-pulse text-[#8B7355]">Loading...</div>
      </div>
    );
  }

  if (!admin) return null;

  return (
    <div className="min-h-screen bg-[#F5F3EF]" data-testid="admin-leads-page">
      {/* Mobile Header */}
      <div className="lg:hidden bg-white border-b border-[#E0DED8] px-4 py-3 flex items-center justify-between">
        <img src="/byron-logo-transparent-26.svg" alt="Byrōn" className="h-5" />
        <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2">
          {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <AdminSidebar
          admin={admin}
          onLogout={handleLogout}
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          showUserInfo={false}
        />

        {/* Main Content */}
        <main className="flex-1 p-6 lg:p-10 min-h-screen">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-2xl font-heading text-[#1A1A1A]">Leads</h1>
            <p className="text-sm text-[#6B6B6B] mt-1">Manage all form submissions and inquiries</p>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-sm border border-[#E0DED8] p-4 mb-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9B9B9B]" />
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="w-full pl-10 pr-4 py-2 border border-[#E0DED8] rounded-sm focus:outline-none focus:border-[#8B7355]"
                  data-testid="search-input"
                />
              </div>
              <select
                value={filters.form_type}
                onChange={(e) => setFilters({ ...filters, form_type: e.target.value })}
                className="px-4 py-2 border border-[#E0DED8] rounded-sm focus:outline-none focus:border-[#8B7355] text-sm"
                data-testid="form-type-filter"
              >
                <option value="">All Form Types</option>
                <option value="contact">Contact</option>
                <option value="off_market">Off Market</option>
                <option value="estimation">Estimation</option>
                <option value="property_inquiry">Property Inquiry</option>
                <option value="acheter_search">Buyer Search</option>
                <option value="louer_search">Rental Search</option>
                <option value="recherche">Advanced Search</option>
              </select>
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className="px-4 py-2 border border-[#E0DED8] rounded-sm focus:outline-none focus:border-[#8B7355] text-sm"
                data-testid="status-filter"
              >
                <option value="">All Status</option>
                <option value="new">New</option>
                <option value="contacted">Contacted</option>
                <option value="qualified">Qualified</option>
                <option value="archived">Archived</option>
              </select>
              <input
                type="date"
                value={filters.date_from}
                onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                className="px-4 py-2 border border-[#E0DED8] rounded-sm focus:outline-none focus:border-[#8B7355] text-sm"
                data-testid="date-from-filter"
              />
              <input
                type="date"
                value={filters.date_to}
                onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                className="px-4 py-2 border border-[#E0DED8] rounded-sm focus:outline-none focus:border-[#8B7355] text-sm"
                data-testid="date-to-filter"
              />
              <select
                value={filters.property_id}
                onChange={(e) => setFilters({ ...filters, property_id: e.target.value })}
                className="px-4 py-2 border border-[#E0DED8] rounded-sm focus:outline-none focus:border-[#8B7355] text-sm"
                data-testid="property-filter"
              >
                <option value="">All Properties</option>
                {properties.map((property) => (
                  <option key={property.id} value={property.id}>
                    {property.title}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Leads List */}
          <div className="bg-white rounded-sm border border-[#E0DED8]">
            {leads.length === 0 ? (
              <div className="p-10 text-center text-[#9B9B9B]">
                <Users size={40} className="mx-auto mb-3 opacity-30" />
                <p>No leads found</p>
              </div>
            ) : (
              <div className="divide-y divide-[#E0DED8]">
                {leads.map((lead) => (
                  <Link
                    key={lead.id}
                    to={`/admin/leads/${lead.id}`}
                    className="flex flex-col sm:flex-row sm:items-center justify-between p-4 sm:px-6 hover:bg-[#F5F3EF] transition-colors gap-3"
                    data-testid={`lead-${lead.id}`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3 flex-wrap">
                        <p className="font-medium text-[#1A1A1A]">
                          {lead.first_name || lead.last_name || lead.name
                            ? `${lead.first_name || ''} ${lead.last_name || ''}`.trim() || lead.name
                            : 'Search activity'}
                        </p>
                        <span className={`inline-block px-2 py-0.5 text-xs rounded ${statusColors[lead.status] || statusColors.new}`}>
                          {lead.status}
                        </span>
                      </div>
                      <p className="text-sm text-[#6B6B6B] mt-1">{lead.email || 'No contact email'}</p>
                      {lead.property_title && (
                        <p className="text-xs text-[#8B7355] mt-1">
                          Property: {lead.property_title}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="px-2 py-1 bg-[#F5F3EF] text-[#6B6B6B] rounded text-xs">
                        {formTypeLabels[lead.form_type] || lead.form_type}
                      </span>
                      <span className="text-[#9B9B9B] flex items-center gap-1 text-xs">
                        <Clock size={12} />
                        {new Date(lead.created_at).toLocaleDateString('fr-CH')}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

// Lead Detail Component
export const AdminLeadDetail = () => {
  const { admin, logout, loading: authLoading } = useAdminAuth();
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (!authLoading && !admin) {
      navigate('/admin/login');
    }
  }, [admin, authLoading, navigate]);

  useEffect(() => {
    if (admin && leadId) {
      fetchLead();
    }
  }, [admin, leadId]);

  const fetchLead = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/admin/leads/${leadId}`,
        { withCredentials: true }
      );
      setLead(response.data);
      setStatus(response.data.status);
      setNotes(response.data.internal_notes || '');
    } catch (error) {
      console.error('Error fetching lead:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API_BASE_URL}/api/admin/leads/${leadId}`,
        { status, internal_notes: notes },
        { withCredentials: true }
      );
      setLead({ ...lead, status, internal_notes: notes });
    } catch (error) {
      console.error('Error saving lead:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#F5F3EF] flex items-center justify-center">
        <div className="animate-pulse text-[#8B7355]">Loading...</div>
      </div>
    );
  }

  if (!admin || !lead) return null;

  return (
    <div className="min-h-screen bg-[#F5F3EF]" data-testid="admin-lead-detail">
      <div className="lg:hidden bg-white border-b border-[#E0DED8] px-4 py-3 flex items-center justify-between">
        <img src="/byron-logo-transparent-26.svg" alt="Byrōn" className="h-5" />
        <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2">
          {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <div className="flex">
        <AdminSidebar
          admin={admin}
          onLogout={handleLogout}
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          showUserInfo={false}
        />

        {/* Main Content */}
        <main className="flex-1 p-6 lg:p-10">
          {/* Back Button */}
          <Link
            to="/admin/leads"
            className="inline-flex items-center gap-2 text-sm text-[#6B6B6B] hover:text-[#1A1A1A] mb-6"
          >
            <ChevronLeft size={16} />
            Back to Leads
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Lead Info */}
            <div className="lg:col-span-2 space-y-6">
              {/* Header */}
              <div className="bg-white rounded-sm border border-[#E0DED8] p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h1 className="text-xl font-heading text-[#1A1A1A]">
                      {lead.first_name || lead.last_name || lead.name
                        ? `${lead.first_name || ''} ${lead.last_name || ''}`.trim() || lead.name
                        : 'Search activity'}
                    </h1>
                    <p className="text-sm text-[#6B6B6B] mt-1">
                      {formTypeLabels[lead.form_type] || lead.form_type}
                    </p>
                  </div>
                  <span className={`px-3 py-1 text-sm rounded ${statusColors[lead.status] || statusColors.new}`}>
                    {lead.status}
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                  {lead.email && (
                    <div className="flex items-center gap-2 text-[#6B6B6B]">
                      <Mail size={16} className="text-[#8B7355]" />
                      <a href={`mailto:${lead.email}`} className="hover:text-[#1A1A1A]">{lead.email}</a>
                    </div>
                  )}
                  {lead.phone && (
                    <div className="flex items-center gap-2 text-[#6B6B6B]">
                      <Phone size={16} className="text-[#8B7355]" />
                      <a href={`tel:${lead.phone}`} className="hover:text-[#1A1A1A]">{lead.phone}</a>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-[#6B6B6B]">
                    <Clock size={16} className="text-[#8B7355]" />
                    {new Date(lead.created_at).toLocaleString('fr-CH')}
                  </div>
                  {lead.source_page && (
                    <div className="flex items-center gap-2 text-[#6B6B6B]">
                      <MapPin size={16} className="text-[#8B7355]" />
                      {lead.source_page}
                    </div>
                  )}
                </div>

                {lead.property_title && (
                  <div className="mt-4 pt-4 border-t border-[#E0DED8]">
                    <p className="text-xs uppercase tracking-[0.1em] text-[#9B9B9B] mb-1">Linked Property</p>
                    <p className="text-sm font-medium text-[#1A1A1A]">{lead.property_title}</p>
                  </div>
                )}
              </div>

              {/* Message */}
              {lead.message && (
                <div className="bg-white rounded-sm border border-[#E0DED8] p-6">
                  <h3 className="text-xs uppercase tracking-[0.15em] text-[#6B6B6B] mb-3 flex items-center gap-2">
                    <FileText size={14} />
                    Message
                  </h3>
                  <p className="text-sm text-[#1A1A1A] whitespace-pre-wrap">{lead.message}</p>
                </div>
              )}

              {/* Submitted Details */}
              {lead.form_data && Object.keys(lead.form_data).length > 0 && (
                <div className="bg-white rounded-sm border border-[#E0DED8] p-6">
                  <h3 className="text-xs uppercase tracking-[0.15em] text-[#6B6B6B] mb-4">Submitted Details</h3>
                  <div className="space-y-3">
                    {Object.entries(lead.form_data).map(([key, value]) => {
                      if (value === null || value === undefined || value === '') return null;
                      const label = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                      const displayValue = Array.isArray(value) ? value.join(', ') : String(value);
                      return (
                        <div key={key} className="flex justify-between py-2 border-b border-[#F5F3EF] last:border-0">
                          <span className="text-sm text-[#6B6B6B]">{label}</span>
                          <span className="text-sm text-[#1A1A1A] font-medium">{displayValue}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Actions Sidebar */}
            <div className="space-y-6">
              {/* Status & Notes */}
              <div className="bg-white rounded-sm border border-[#E0DED8] p-6">
                <h3 className="text-xs uppercase tracking-[0.15em] text-[#6B6B6B] mb-4">Update Lead</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs uppercase tracking-[0.1em] text-[#9B9B9B] mb-2">Status</label>
                    <select
                      value={status}
                      onChange={(e) => setStatus(e.target.value)}
                      className="w-full px-3 py-2 border border-[#E0DED8] rounded-sm focus:outline-none focus:border-[#8B7355] text-sm"
                    >
                      <option value="new">New</option>
                      <option value="contacted">Contacted</option>
                      <option value="qualified">Qualified</option>
                      <option value="archived">Archived</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs uppercase tracking-[0.1em] text-[#9B9B9B] mb-2">Internal Notes</label>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={4}
                      className="w-full px-3 py-2 border border-[#E0DED8] rounded-sm focus:outline-none focus:border-[#8B7355] text-sm resize-none"
                      placeholder="Add private notes about this lead..."
                    />
                  </div>

                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-[#1A1A1A] text-white text-sm rounded-sm hover:bg-[#333] transition-colors disabled:opacity-50"
                  >
                    <Save size={16} />
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="bg-white rounded-sm border border-[#E0DED8] p-6">
                <h3 className="text-xs uppercase tracking-[0.15em] text-[#6B6B6B] mb-4">Quick Actions</h3>
                <div className="space-y-2">
                  <a
                    href={`mailto:${lead.email}`}
                    className="flex items-center gap-2 px-4 py-2 border border-[#E0DED8] rounded-sm text-sm text-[#6B6B6B] hover:bg-[#F5F3EF] transition-colors w-full"
                  >
                    <Mail size={16} />
                    Send Email
                  </a>
                  {lead.phone && (
                    <a
                      href={`tel:${lead.phone}`}
                      className="flex items-center gap-2 px-4 py-2 border border-[#E0DED8] rounded-sm text-sm text-[#6B6B6B] hover:bg-[#F5F3EF] transition-colors w-full"
                    >
                      <Phone size={16} />
                      Call
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>

      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default AdminLeads;
